import type { ColDef, ICellRendererParams } from '../../../components/ui/table';
import { formatAuditDateTime } from '../../../lib/auditColumnDefs';
import { collectUniqueScalarValues } from '../../../components/shared/GridColumnFilterPanel';
import {
  CopyableGridText,
  EmptyGridCell,
  GRID_CHIP_TONE,
  GridCellWrap,
  GridChip,
  accentAvatarStyle,
  type ChipTone,
} from '../../../components/shared/agGridCells';
import { Avatar, AvatarFallback } from '../../../components/ui/avatar';
import type {
  AuditLogColumnFilterKey,
  AuditLogRow,
} from '../models/types';

type AuditLogFilterColumnConfig = {
  key: AuditLogColumnFilterKey;
  label: string;
  type?: 'select' | 'dateRange';
  getOptions?: (rows: AuditLogRow[]) => string[];
};

export const AUDIT_LOG_FILTER_COLUMNS: AuditLogFilterColumnConfig[] = [
  {
    key: 'userName',
    label: 'User Name',
    getOptions: (rows) => collectUniqueScalarValues(rows, (row) => row.userName),
  },
  {
    key: 'userType',
    label: 'User Type',
    getOptions: (rows) => collectUniqueScalarValues(rows, (row) => row.userType),
  },
  {
    key: 'action',
    label: 'Action',
    getOptions: (rows) => collectUniqueScalarValues(rows, (row) => row.action),
  },
  {
    key: 'entityName',
    label: 'Entity',
    getOptions: (rows) => collectUniqueScalarValues(rows, (row) => row.entityName),
  },
  {
    key: 'recordId',
    label: 'Record ID',
    getOptions: (rows) => collectUniqueScalarValues(rows, (row) => row.recordId),
  },
  {
    key: 'ipAddress',
    label: 'IP Address',
    getOptions: (rows) => collectUniqueScalarValues(rows, (row) => row.ipAddress),
  },
  {
    key: 'createdAt',
    label: 'Timestamp',
    type: 'dateRange',
  },
];

export const AUDIT_LOG_COLUMN_LABELS = Object.fromEntries(
  AUDIT_LOG_FILTER_COLUMNS.map(({ key, label }) => [key, label]),
) as Record<AuditLogColumnFilterKey, string>;

function columnLabel(key: AuditLogColumnFilterKey) {
  return AUDIT_LOG_COLUMN_LABELS[key];
}

export function buildAuditLogColumnFilterFields(sourceRows: AuditLogRow[]) {
  return AUDIT_LOG_FILTER_COLUMNS.map(({ key, label, getOptions, type }) => ({
    key,
    label,
    type: type ?? 'select',
    options: type === 'dateRange' ? [] : (getOptions?.(sourceRows) ?? []),
  }));
}

const ACTION_CHIP_TONE: Record<string, ChipTone> = {
  create: { bg: 'rgba(46,213,115,0.15)', color: '#2ed573' },
  add: { bg: 'rgba(46,213,115,0.15)', color: '#2ed573' },
  insert: { bg: 'rgba(46,213,115,0.15)', color: '#2ed573' },
  update: { bg: 'rgba(91,127,255,0.15)', color: '#5B7FFF' },
  edit: { bg: 'rgba(91,127,255,0.15)', color: '#5B7FFF' },
  modify: { bg: 'rgba(91,127,255,0.15)', color: '#5B7FFF' },
  delete: { bg: 'rgba(255,71,87,0.15)', color: '#ff4757' },
  remove: { bg: 'rgba(255,71,87,0.15)', color: '#ff4757' },
  login: { bg: 'rgba(167,139,250,0.15)', color: '#a78bfa' },
  logout: GRID_CHIP_TONE.neutral,
  view: GRID_CHIP_TONE.terminal,
  read: GRID_CHIP_TONE.terminal,
};

function actionTone(action: string): ChipTone {
  const key = action.trim().toLowerCase();
  return ACTION_CHIP_TONE[key] ?? GRID_CHIP_TONE.category;
}

function actorInitials(name: string): string {
  return (
    name
      .split(' ')
      .filter(Boolean)
      .map((word) => word[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() || '??'
  );
}

function ActorCellRenderer(params: ICellRendererParams<AuditLogRow>) {
  const row = params.data;
  if (!row) return null;
  const name = row.userName;
  if (!name || name === '—') {
    return (
      <GridCellWrap>
        <EmptyGridCell />
      </GridCellWrap>
    );
  }

  return (
    <GridCellWrap>
      <Avatar className="h-7 w-7 shrink-0">
        <AvatarFallback
          className="text-[0.65rem] font-semibold leading-none"
          style={accentAvatarStyle(row.accent)}
        >
          {actorInitials(name)}
        </AvatarFallback>
      </Avatar>
      <CopyableGridText
        text={name}
        copyValue={name}
        textClassName="text-muted-foreground"
        copyMessage="User name copied to clipboard"
        copyLabel="Copy user name"
      />
    </GridCellWrap>
  );
}

function EntityCellRenderer(params: ICellRendererParams<AuditLogRow, string>) {
  const value = params.value?.trim();
  if (!value || value === '—') {
    return (
      <GridCellWrap>
        <EmptyGridCell />
      </GridCellWrap>
    );
  }
  return (
    <GridCellWrap>
      <CopyableGridText
        text={value}
        textClassName="text-muted-foreground"
        copyMessage="Entity copied to clipboard"
        copyLabel="Copy entity"
      />
    </GridCellWrap>
  );
}

function ActionChipCellRenderer(params: ICellRendererParams<AuditLogRow, string>) {
  const value = params.value;
  if (!value || value === '—') {
    return (
      <GridCellWrap>
        <EmptyGridCell />
      </GridCellWrap>
    );
  }
  return (
    <GridCellWrap>
      <GridChip label={value} tone={actionTone(value)} mono />
    </GridCellWrap>
  );
}

function DescriptionCellRenderer(params: ICellRendererParams<AuditLogRow, string>) {
  const description =
    params.value && params.value !== '—' ? String(params.value) : '—';

  if (description === '—') {
    return (
      <GridCellWrap>
        <span className="text-muted-foreground">—</span>
      </GridCellWrap>
    );
  }

  return (
    <GridCellWrap>
      <CopyableGridText
        text={description}
        textClassName="text-muted-foreground"
        copyMessage="Description copied to clipboard"
        copyLabel="Copy description"
      />
    </GridCellWrap>
  );
}

function IpCellRenderer(params: ICellRendererParams<AuditLogRow, string>) {
  const value = params.value?.trim();
  if (!value || value === '—') {
    return (
      <GridCellWrap>
        <EmptyGridCell />
      </GridCellWrap>
    );
  }
  return (
    <GridCellWrap>
      <CopyableGridText
        text={value}
        textClassName="font-mono text-muted-foreground"
        copyMessage="IP address copied to clipboard"
        copyLabel="Copy IP"
      />
    </GridCellWrap>
  );
}

export function createAuditLogColumnDefs(): ColDef<AuditLogRow>[] {
  return [
    {
      field: 'userName',
      colId: 'userName',
      headerName: columnLabel('userName'),
      width: 240,
      minWidth: 180,
      filterValueGetter: (params) => params.data?.userName ?? '',
      cellRenderer: ActorCellRenderer,
    },
    {
      field: 'userType',
      colId: 'userType',
      headerName: columnLabel('userType'),
      width: 140,
      cellRenderer: (params: ICellRendererParams<AuditLogRow, string>) => {
        const value = params.value;
        if (!value || value === '—') {
          return (
            <GridCellWrap>
              <EmptyGridCell />
            </GridCellWrap>
          );
        }
        return (
          <GridCellWrap>
            <GridChip label={value} tone={GRID_CHIP_TONE.category} />
          </GridCellWrap>
        );
      },
    },
    {
      field: 'action',
      colId: 'action',
      headerName: columnLabel('action'),
      width: 140,
      cellRenderer: ActionChipCellRenderer,
    },
    {
      field: 'entityName',
      colId: 'entityName',
      headerName: columnLabel('entityName'),
      width: 200,
      minWidth: 160,
      filterValueGetter: (params) => params.data?.entityName ?? '',
      cellRenderer: EntityCellRenderer,
    },
    {
      field: 'recordId',
      colId: 'recordId',
      headerName: columnLabel('recordId'),
      width: 130,
      cellRenderer: (params: ICellRendererParams<AuditLogRow, string>) => {
        const value = params.value?.trim();
        if (!value || value === '—') {
          return (
            <GridCellWrap>
              <EmptyGridCell />
            </GridCellWrap>
          );
        }
        return (
          <GridCellWrap>
            <CopyableGridText
              text={value}
              textClassName="font-mono text-muted-foreground"
              copyMessage="Record ID copied to clipboard"
              copyLabel="Copy record ID"
            />
          </GridCellWrap>
        );
      },
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 420,
      minWidth: 350,
      wrapText: false,
      autoHeight: false,
      cellClass: 'logs-description',
      cellRenderer: DescriptionCellRenderer,
      tooltipValueGetter: (params) => {
        const value = params.value;
        if (!value || value === '—') return undefined;
        return String(value);
      },
      valueFormatter: (params) =>
        params.value && params.value !== '—' ? String(params.value) : '—',
    },
    {
      field: 'ipAddress',
      colId: 'ipAddress',
      headerName: columnLabel('ipAddress'),
      width: 150,
      cellRenderer: IpCellRenderer,
    },
    {
      colId: 'createdAt',
      field: 'createdAt',
      headerName: columnLabel('createdAt'),
      width: 180,
      cellClass: 'text-muted-foreground',
      valueGetter: (params) => params.data?.createdAt ?? null,
      valueFormatter: (params) => formatAuditDateTime(String(params.value ?? '')),
      filter: {
        component: 'agDateColumnFilter',
        doesFilterPass: () => true,
      },
      filterParams: {
        filterOptions: ['equals', 'inRange'],
        maxNumConditions: 1,
        buttons: ['clear', 'apply'],
        closeOnApply: true,
      },
    },
  ];
}
